import { isEmpty } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import Lang from '/@/modules/core/app/services/Lang';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { ContactEmployment } from '/@/modules/contact/models/ContactEmployment';
import { maxBy } from 'lodash';
import { PredefinedCountry } from '/@/modules/predefined/enums/PredefinedCountry';
import { PredefinedJurisdiction } from '/@/modules/predefined/enums/PredefinedJurisdiction';
import { reduceJoin } from '/@/modules/core/app/helpers/StringHelper';
import { MaritalStatus } from '/@/modules/contact/enums/MaritalStatus';
import { EmploymentStatus } from '/@/modules/contact/enums/EmploymentStatus';
import { Address } from '/@/modules/address/models/Address';
import { ContactAlias } from '/@/modules/contact/models/ContactAlias';
import { ModelHasAsset } from '/@/modules/contact/models/ModelHasAsset';
import { Country } from '/@/modules/predefined/models/Country';
import { ContactCountryResidency } from '@/modules/contact/models/ContactCountryResidency';
import { ContactRelationship } from '@/modules/contact/models/ContactRelationship';
import { User } from '@/modules/user/models/User';
import { fullMonthDayYearDateFormat } from '@/modules/core/app/helpers/DateHelper';
import { ContactEmploymentDetail } from '@/modules/contact/models/ContactEmploymentDetail';
import { joinWithOxfordComma } from '/@/modules/core/app/helpers/ArrayHelper';
import { Gender } from '@/modules/contact/enums/Gender';
import { PreferredPronouns } from '@/modules/contact/enums/PreferredPronouns';
import { ModelHasLiability } from '@/modules/contact/models/ModelHasLiability';
import { ContactHardAssetDetail } from '@/modules/contact/models/ContactHardAssetDetail';
import { ContactDigitalAssetDetail } from '@/modules/contact/models/ContactDigitalAssetDetail';
import { ContactMarriageHistory } from '@/modules/contact/models/ContactMarriageHistory';

export class Contact extends BaseEntityModel {
  id?: number;
  userId?: number;
  estateId?: number;
  email?: string;
  title?: string;
  firstName?: string;
  middleName?: string;
  birthSurname?: string;
  lastName?: string;
  personTitle?: string;
  phoneNumber?: string;
  aboutEn?: string;
  aboutFr?: string;
  extension?: string;
  dateOfBirth?: string;
  dateOfDeath?: string;
  lastThreeDigitsNationalIdentificationNumber?: string;
  nationalIdentificationNumber?: string;
  healthInsuranceNumber?: string;
  gender?: string;
  preferredPronouns?: string;
  maritalStatus?: string;
  employmentStatus?: string;
  employmentDetails?: string;
  permanentResidentCountryId?: number;
  permanentResidentDetails?: string;
  hasUnknownFather?: boolean;
  hasUnknownMother?: boolean;
  isDeceased?: boolean;
  isRegisteredStatusIndianAct?: boolean;
  ordinarilyResidesOnReserve?: boolean;
  address?: Relationship<Address | null>;
  birthAddress?: Relationship<Address> | null;
  deathAddress?: Relationship<Address> | null;
  contactEmployments?: Relationship<ContactEmployment[]> | null;
  contactAliases?: Relationship<ContactAlias[]> | null;
  pivot?: Relationship<ModelHasAsset> | null;
  citizenships?: Relationship<Country>[] | null;
  contactCountryResidencies?: Relationship<ContactCountryResidency[]>;
  relationships?: Relationship<ContactRelationship[]> | null;
  user?: Relationship<User> | null;
  contactEmploymentDetail: Relationship<ContactEmploymentDetail> | null;
  contactHardAssetDetail: Relationship<ContactHardAssetDetail> | null;
  contactDigitalAssetDetail: Relationship<ContactDigitalAssetDetail> | null;
  parents?: Relationship<Contact[]> | null;
  contactMarriages?: ContactMarriageHistory[] | null;

  constructor(model: Partial<Contact> = {}) {
    super(model);

    this.id = model.id;
    this.userId = model.userId;
    this.estateId = model.estateId;
    this.email = model.email;
    this.firstName = model.firstName;
    this.middleName = model.middleName;
    this.birthSurname = model.birthSurname;
    this.lastName = model.lastName;
    this.personTitle = model.personTitle;
    this.phoneNumber = model.phoneNumber ?? '';
    this.aboutEn = model.aboutEn;
    this.aboutFr = model.aboutFr;
    this.extension = model.extension ?? '';
    this.dateOfBirth = model.dateOfBirth;
    this.dateOfDeath = model.dateOfDeath;
    this.lastThreeDigitsNationalIdentificationNumber =
      model.lastThreeDigitsNationalIdentificationNumber;
    this.nationalIdentificationNumber = model.nationalIdentificationNumber;
    this.healthInsuranceNumber = model.healthInsuranceNumber ?? '';
    this.gender = model.gender;
    this.preferredPronouns = model.preferredPronouns;
    this.maritalStatus = model.maritalStatus;
    this.employmentStatus = model.employmentStatus;
    this.permanentResidentCountryId = model.permanentResidentCountryId;
    this.permanentResidentDetails = model.permanentResidentDetails ?? '';
    this.employmentDetails = model.employmentDetails;
    this.hasUnknownFather = model.hasUnknownFather;
    this.hasUnknownMother = model.hasUnknownMother;
    this.isDeceased = model.isDeceased;
    this.isRegisteredStatusIndianAct = model.isRegisteredStatusIndianAct;
    this.ordinarilyResidesOnReserve = model.ordinarilyResidesOnReserve;

    this.address = Address.mapModel(model.address);
    this.birthAddress = Address.mapModelOrNull(model.birthAddress);
    this.deathAddress = Address.mapModelOrNull(model.deathAddress);
    this.pivot =
      model.pivot && Object.keys(model.pivot).includes('liabilityId')
        ? ModelHasLiability.mapModelOrNull(model.pivot)
        : ModelHasAsset.mapModelOrNull(model.pivot);
    this.contactEmployments = ContactEmployment.mapModelsOrNull(model.contactEmployments);
    this.contactAliases = ContactAlias.mapModelsOrNull(model.contactAliases);
    this.citizenships = Country.mapModelsOrNull(model.citizenships);
    this.contactCountryResidencies =
      ContactCountryResidency.mapModelsOrNull(model.contactCountryResidencies) ?? undefined;
    this.relationships = ContactRelationship.mapModelsOrNull(model.relationships);
    this.user = User.mapModelOrNull(model.user);
    this.contactEmploymentDetail = ContactEmploymentDetail.mapModelOrNull(
      model.contactEmploymentDetail
    );
    this.contactHardAssetDetail = ContactHardAssetDetail.mapModelOrNull(
      model.contactHardAssetDetail
    );
    this.contactDigitalAssetDetail = ContactDigitalAssetDetail.mapModelOrNull(
      model.contactDigitalAssetDetail
    );
    this.parents = Contact.mapModelsOrNull(model.parents);
    this.contactMarriages = ContactMarriageHistory.mapModelsOrNull(model.contactMarriages);
  }

  getModelName(): ModelType {
    return ModelType.CONTACT;
  }

  get age(): number | null {
    if (!this.dateOfBirth) {
      return null;
    }
    const birthDate: Dayjs = dayjs(this.dateOfBirth);
    const maxDate: Dayjs = this.dateOfDeath ? dayjs(this.dateOfDeath) : dayjs();
    return maxDate.diff(birthDate, 'year');
  }

  get formattedAge(): number | string | null {
    if (!this.age && this.age !== 0) {
      return null;
    }
    return this.age >= 1
      ? Math.floor(this.age)
      : `${this.ageInNumberOfMonths} ${Lang.t('contact.months')}`;
  }

  get ageInNumberOfMonths(): number | null {
    if (!this.dateOfBirth) {
      return null;
    }
    const birthDate: Dayjs = dayjs(this.dateOfBirth);
    const maxDate: Dayjs = this.dateOfDeath ? dayjs(this.dateOfDeath) : dayjs();
    return maxDate.diff(birthDate, 'month');
  }

  ageOnDate(date: Date): number | null {
    if (!this.dateOfBirth) {
      return null;
    }
    return dayjs(date).diff(dayjs(this.dateOfBirth), 'year');
  }

  get isAMinor(): boolean {
    if (this.age === null) {
      return false;
    }
    return !!(this.dateOfBirth && this.age < 18);
  }

  get isAnAdult(): boolean {
    return !!(this.dateOfBirth && this.age && this.age >= 18);
  }

  get is18To25(): boolean {
    return !!(this.dateOfBirth && this.isAnAdult && this.isLessThan26);
  }

  get isLessThan26(): boolean {
    return !!(this.dateOfBirth && this.age && this.age < 26);
  }

  get fullName(): string {
    return reduceJoin([this.firstName ?? '', this.middleName ?? '', this.lastName ?? '']);
  }

  get firstLetters(): string {
    if (!this.firstName && !this.lastName) {
      return '';
    }

    return `${this.firstName?.charAt(0) ?? ''}${this.lastName?.charAt(0) ?? ''}`;
  }

  get initials(): string {
    return this.firstLetters.toUpperCase();
  }

  // @ts-ignore
  get onGoingEmployment(): ContactEmployment | undefined {
    return this.contactEmployments?.find(
      (contactEmployment: ContactEmployment) => !contactEmployment.endDate
    );
  }

  set onGoingEmployment(employment: ContactEmployment) {
    this.setEmployment(employment);
  }

  get hasEmployment(): boolean {
    if (!this.contactEmployments?.length) {
      return false;
    }
    return this.contactEmployments?.length > 0;
  }

  // @ts-ignore
  get mostRecentEmployment(): ContactEmployment | undefined {
    return (
      this.onGoingEmployment ??
      maxBy(this.contactEmployments, (employment: ContactEmployment) =>
        employment.endDate ? new Date(employment.endDate).getTime() : null
      )
    );
  }

  set mostRecentEmployment(employment: ContactEmployment) {
    this.setEmployment(employment);
  }

  setEmployment(employment: ContactEmployment): void {
    if (!this.contactEmployments?.length) {
      this.contactEmployments = [employment];
    } else {
      let index: number = this.contactEmployments?.findIndex(
        (employment: ContactEmployment) => employment === this.mostRecentEmployment
      );
      index = index > -1 ? index : this.contactEmployments.length;
      this.contactEmployments[index] = employment;
    }
  }

  get isBornInCanada(): boolean {
    return this.birthAddress?.countryId === PredefinedCountry.CANADA.id;
  }

  get isBornInUS(): boolean {
    return this.birthAddress?.countryId === PredefinedCountry.UNITED_STATES.id;
  }

  get diedInCanada(): boolean {
    return this.deathAddress?.countryId === PredefinedCountry.CANADA.id;
  }

  get diedInUS(): boolean {
    return this.deathAddress?.countryId === PredefinedCountry.UNITED_STATES.id;
  }

  get isMarried(): boolean {
    return this.maritalStatus === MaritalStatus.MARRIED.key;
  }

  get isDivorced(): boolean {
    return this.maritalStatus === MaritalStatus.DIVORCED.key;
  }

  get isWidowed(): boolean {
    return this.maritalStatus === MaritalStatus.WIDOWED.key;
  }

  get hasCommonLawPartner(): boolean {
    return this.maritalStatus === MaritalStatus.COMMON_LAW_PARTNER.key;
  }

  get hasPartner(): boolean {
    return this.isMarried || this.hasCommonLawPartner;
  }

  get phoneNumberWithExtension(): string | undefined {
    return this.extension ? `${this.phoneNumber} ext ${this.extension}` : this.phoneNumber;
  }

  get birthCountry(): Country {
    return this.birthAddress?.country as Country;
  }

  getBirthArea(): string {
    return this.birthAddress?.jurisdictionId
      ? (PredefinedJurisdiction.getByKey(this.birthAddress.jurisdictionId)?.name as string)
      : (this.birthAddress?.administrativeDivision as string);
  }

  getBirthCity(): string {
    return this.birthAddress?.city as string;
  }

  get isRetired(): boolean {
    return this.employmentStatus === EmploymentStatus.RETIRED.key;
  }

  get hasContactAliases(): boolean {
    return !!this.contactAliases?.length;
  }

  get primaryContactAlias(): ContactAlias | undefined | null {
    return this.hasContactAliases
      ? this.contactAliases?.find((alias: ContactAlias) => alias.isPrimary)
      : null;
  }

  get summaryEmploymentSituation(): string | null {
    return this.employmentStatus && this.employmentDetails
      ? EmploymentStatus.getByKey(this.employmentStatus).value +
          (this.employmentDetails ? `, ${this.employmentDetails}` : '')
      : null;
  }

  get maritalStatusByValue(): string {
    if (!this.maritalStatus) {
      return '';
    }

    return MaritalStatus.getByKey(this.maritalStatus).value;
  }

  get formattedFullMonthDayYearDateOfBirth(): string {
    return this.dateOfBirth
      ? fullMonthDayYearDateFormat(this.dateOfBirth)
      : Lang.t('core.non-available');
  }

  get formattedCitizenships(): string | undefined {
    return this.citizenships?.map((item: Country) => item.name)?.join(',');
  }

  get isPermanentResidentOfCanada(): boolean {
    return this.permanentResidentCountryId === PredefinedCountry.CANADA.id;
  }

  get displayContactAliases(): string | undefined {
    return this.contactAliases?.length
      ? joinWithOxfordComma(this.contactAliases?.map((item: ContactAlias) => item.fullName ?? ''))
      : undefined;
  }

  get displayGender(): string {
    return Gender.getByKey(this.gender)?.value ?? '';
  }

  get displayPreferredPronouns(): string {
    return this.preferredPronouns
      ? PreferredPronouns.getByKey(this.preferredPronouns)?.value ?? ''
      : '';
  }

  get shouldShowFirstNationsDisclaimers(): boolean {
    return !!this.isRegisteredStatusIndianAct && !!this.ordinarilyResidesOnReserve;
  }

  get isCompleted(): boolean {
    return !isEmpty(this.firstName) && !isEmpty(this.lastName) && !isEmpty(this.dateOfBirth);
  }
}
